import { call, put, takeLatest, delay } from 'redux-saga/effects';
import axios from 'axios';
import {
  UPLOAD_REQUEST, uploadFileSuccess, uploadFileFailure, uploadProgress
} from '../../actions/ingestion/upload';
import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { Error as ErrorAction } from '../../actions/error/error';
import { 
  processApiResponse, 
  handleSagaError, 
  createSuccessPayload, 
  createErrorPayload 
} from '../../../utils/apiResponseHandler';

// Helper function to get headers with workspace ID
function getHeaders() {
  const userHeaders = getLocalUser();
  // getLocalUser() already includes X-WORKSPACE-ID from localStorage
  return userHeaders;
}

const getExecutionFromResponse = (response) => {
  return response?.data?.execution || response?.data?.data?.execution || response?.data?.data || response?.data;
};

// Upload file saga
function* uploadFileSaga(action) {
  try {
    const headers = getHeaders();
    const { file, columnTypes, options = {} } = action.payload;
    
    // Create FormData with enhanced schema
    const formData = new FormData();
    formData.append('file', file);
    
    // Only send the enhanced column types (includes renaming and date formats)
    formData.append('columnTypes', JSON.stringify(columnTypes));

    // Optional: cascade-delete existing data in the same time range
    // before this import. Backend reads `deleteExisting` form param
    // and translates it to the `delete_existing` job_config flag,
    // which the ingestion worker uses to call the cascading
    // delete_signals() across signals + all rollups.
    if (options.deleteExisting) {
      formData.append('deleteExisting', 'true');
    }
    
    // Remove Content-Type header to let browser set it for multipart/form-data
    const { 'Content-Type': contentType, ...uploadHeaders } = headers;
    
    const response = yield call(
      axios.post,
      `${config.API_URL}/ingestion/sources/upload`,
      formData,
      { 
        headers: uploadHeaders,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Note: This won't work in saga, but we can handle progress differently
          // For now, we'll just track completion
        }
      }
    );
    
    // Use unified response processing
    const result = processApiResponse(response, 'file upload');
    
    if (result.success) {
      const responseData = result.data;
      const execution = responseData?.execution || responseData?.data?.execution;
      
      // Handle 202 Accepted response with execution_id (new API)
      if (responseData.status === 'queued' && responseData.execution_id) {
        yield put(uploadFileSuccess(createSuccessPayload({
          status: 'queued',
          execution_id: responseData.execution_id,
          message: responseData.message,
          filename: responseData.filename,
          file_size_bytes: responseData.file_size_bytes,
          execution
        })));
        
        yield put({ 
          type: 'SHOW_SUCCESS_SNACKBAR', 
          message: 'File uploaded successfully! Processing in background...'
        });
        
        // Start polling for execution status
        yield call(pollExecutionStatus, responseData.execution_id);
      } else if (responseData.status === 'success' && responseData.rows_inserted) {
        // Handle immediate success (backwards compatibility)
        const { rows_inserted, duration_seconds, throughput_records_per_sec } = responseData;
        yield put(uploadFileSuccess(createSuccessPayload(responseData)));
        yield put({ 
          type: 'SHOW_SUCCESS_SNACKBAR', 
          message: `✓ Imported ${rows_inserted.toLocaleString()} rows in ${duration_seconds}s (${throughput_records_per_sec.toLocaleString()} rec/sec)`
        });
      } else if (execution) {
        yield put(uploadFileSuccess(createSuccessPayload({
          status: execution.status || responseData.status || 'processing',
          execution_id: execution.execution_id || responseData.execution_id,
          execution
        })));
      } else {
        // Default success handling
        yield put(uploadFileSuccess(createSuccessPayload(responseData)));
        yield put({ type: 'SHOW_SUCCESS_SNACKBAR', message: result.message });
      }
    } else {
      throw new Error(result.error);
    }
  } catch (error) {
    const errorPayload = createErrorPayload(error, 'file upload');
    yield put(ErrorAction(error));
    yield put(uploadFileFailure(errorPayload));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: errorPayload.message });
  }
}

// Poll execution status until completion
function* pollExecutionStatus(executionId) {
  try {
    let attempts = 0;
    const maxAttempts = 150; // 5 minutes max (150 * 2s = 300s)
    
    while (attempts < maxAttempts) {
      yield delay(2000); // Wait 2 seconds between polls
      attempts++;
      
      const headers = getHeaders();
      const response = yield call(
        axios.get,
        `${config.API_URL}/ingestion/executions/${executionId}`,
        { headers }
      );

      const execution = getExecutionFromResponse(response);

      // Update progress if available
      if (execution?.progress) {
        yield put(uploadProgress(execution.progress.percentage || 0));
      }
      
      // Check if execution completed
      if (execution.status === 'completed') {
        const { rows_inserted, duration_seconds, throughput_records_per_sec } = execution.stats || {};
        
        yield put(uploadFileSuccess({
          status: 'completed',
          execution_id: executionId,
          rows_inserted,
          duration_seconds,
          throughput_records_per_sec,
          execution,
          message: `Imported ${rows_inserted?.toLocaleString() || 0} rows successfully`
        }));
        
        yield put({ 
          type: 'SHOW_SUCCESS_SNACKBAR', 
          message: `✓ Imported ${rows_inserted?.toLocaleString() || 0} rows in ${duration_seconds || 0}s (${throughput_records_per_sec?.toLocaleString() || 0} rec/sec)`
        });
        
        return; // Exit polling
      } else if (execution.status === 'failed') {
        const errorMessage = execution.error?.message || 'Import failed';
        
        yield put(uploadFileFailure({ message: errorMessage }));
        yield put({ type: 'SHOW_ERROR_SNACKBAR', message: errorMessage });
        
        return; // Exit polling
      } else if (execution.status === 'cancelled') {
        yield put(uploadFileFailure({ message: 'Import was cancelled' }));
        yield put({ type: 'SHOW_ERROR_SNACKBAR', message: 'Import was cancelled' });
        
        return; // Exit polling
      }
      
      // Continue polling for processing/queued status
      yield put(uploadFileSuccess({
        status: execution.status || 'processing',
        execution_id: executionId,
        execution
      }));
    }
    
    // Timeout after max attempts
    yield put(uploadFileFailure({ message: 'Import timeout - please check execution status manually' }));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: 'Import timeout - please check execution status manually' });
    
  } catch (error) {
    yield put(uploadFileFailure({ message: 'Failed to check execution status' }));
    yield put({ type: 'SHOW_ERROR_SNACKBAR', message: 'Failed to check execution status' });
  }
}

// Watcher saga
export function* watch() {
  yield takeLatest(UPLOAD_REQUEST, uploadFileSaga);
}
